/**
 * AppointmentDocumentsDialog — a load's paperwork, in one place (#144).
 *
 * ⭐ Pete, 2026-08-20: *"Probably a Scan Paperwork and a View Paperwork - just in
 * case we need to overwrite the other scan."*
 *
 * ⛔ THERE WAS NO PER-APPOINTMENT DOCUMENTS VIEWER ANYWHERE IN THE APP.
 * `getAppointmentDocuments` has existed in `client.ts` since migration 145 and
 * **nothing called it** — so every document filed against a load was invisible
 * from the moment it was filed. `ScanFolderPanel` scans INTO an appointment and
 * `SignDocumentDialog` signs one document; neither answers "what have we got?".
 *
 * ⭐ SCAN *AND* VIEW IN ONE DIALOG, not two buttons. Pete asked for both, and the
 * reason they belong together is his own: *"just in case we need to overwrite the
 * other scan"* — deciding whether to re-scan requires seeing what is already on
 * file. Two separate buttons would make that a decision taken blind.
 *
 * ⚠️ THE TWO-DOCUMENT PAIRING IS THE POINT (#110). An original and its signed copy
 * are two rows, and `signedFromDocumentId` is which-is-which. A flat list would
 * show "BOL.pdf" and "BOL-signed.pdf" as unrelated files and leave a scheduler
 * guessing which one the carrier was given.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertCircle, Camera, Download, Eye, CreditCard, Loader2, PenLine, Printer,
    RefreshCw, Trash2, TruckIcon,
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SignDocumentDialog } from '@/components/SignDocumentDialog';
import {
    ApiCallError, getAppointmentPaperworkDossier, getCheckInDocuments, getDocumentUrl, voidDocumentApi,
    uploadDocument,
    flattenDossierDocuments,
    type DocumentDto, type LicenceSummaryDto, type PreviewTarget,
} from '@/api/client';
import { DocumentPreview } from '@/components/DocumentPreview';
import { DocumentThumb } from '@/components/DocumentThumb';
import { FileDropZone } from '@/components/FileDropZone';
import { printDocumentToDialog } from '@/lib/printDocument';

interface Props {
    open: boolean;
    onClose: () => void;
    appointmentId: string | null;
    /**
     * ⛔ AN ARRIVAL WITH NO LOAD YET, AND THE WHOLE REASON THIS PROP EXISTS.
     *
     * ⭐ Pete, 2026-08-25: paperwork dropped on an unmatched driver card had no
     * viewer at all — the eye icon was hidden precisely on the cards the #221
     * flow puts paperwork on, because opening it needed an appointment.
     *
     * ⚠️ ONE OF THE TWO, NOT BOTH. When there is an appointment the DOSSIER is
     * the right source: it gathers the load's documents AND every arrival's, and
     * showing a subset while claiming "everything filed against this load" is the
     * confident wrong answer this dialog was already fixed once for. This prop is
     * for the case where there IS no load to gather from.
     */
    checkInId?: string | null;
    appointmentNumber?: string | null;
    facilityCode: string;
    carrierCode?: string | null;
    getToken: () => Promise<string>;
    /**
     * ⛔ REMOVED 2026-08-25 — there was an `onScanMore` prop and a "Scan paperwork"
     * button here. Pete: *"I don't think we need the 'Scan Paperwork' button on the
     * right-click paperwork popup. We don't really scan anything from this app."*
     *
     * ⚠️ HE WAS RIGHT AND IT WAS WORSE THAN HE THOUGHT: its only caller passed
     * `() => setDocsFor(null)`, so the button CLOSED THE DIALOG AND DID NOTHING ELSE.
     * A control labelled with an action it does not perform is worse than a missing
     * one — somebody presses it, the screen shuts, and they conclude the feature is
     * broken rather than absent.
     *
     * ⭐ AND NOTHING IN THIS APP SCANS. Paper reaches Dock two ways: the office
     * scanner drops it into the SharePoint folder, or a person drags a file in from
     * their PC. The Zebra scans BARCODES (a driver's licence), which is a different
     * act entirely and lives at the gate.
     */
    /** Something changed — the caller's counts and ticks are now stale. */
    onChanged?: () => void;
}

/** A signed copy shown beneath the original it was made from. */
interface DocGroup {
    original: DocumentDto;
    signed: DocumentDto[];
}

const KB = 1024;
function sizeOf(bytes: number): string {
    if (bytes < KB) return `${bytes} B`;
    if (bytes < KB * KB) return `${Math.round(bytes / KB)} KB`;
    return `${(bytes / (KB * KB)).toFixed(1)} MB`;
}

function when(iso: string | null): string {
    if (!iso) return '—';
    // ⚠️ The browser's zone, which for a scheduler IS the facility's zone. Not
    // worth a timezone lookup here: every other date in this panel does the same,
    // and a mismatch between them would be worse than both being local.
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function AppointmentDocumentsDialog({
    open, onClose, appointmentId, checkInId, appointmentNumber, facilityCode, carrierCode,
    getToken, onChanged,
}: Props) {
    const [docs, setDocs] = useState<DocumentDto[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    /** ⛔ Neutral, not red — "opened in a new tab" and "this type needs Acrobat"
        are both correct answers, and the print dialog often opens BEHIND this
        window, so without a line here a working button looks like a dead one. */
    const [printMsg, setPrintMsg] = useState<string | null>(null);

    const [signing, setSigning] = useState<DocumentDto | null>(null);
    const [voiding, setVoiding] = useState<DocumentDto | null>(null);
    /**
     * ⭐ THE DRIVERS' LICENCES, ON THE LOAD — Pete, 2026-08-25: *"with the license,
     * scanned paperwork and scanned images for the appointment, we need a spot on the
     * appointment... where it says view all paperwork for the order."*
     *
     * ⛔ DECODED FIELDS ONLY, NEVER THE IMAGE AND NEVER THE RAW BARCODE. Pete settled
     * this when the licence rules were written — *"Decode the license data and store it
     * for the driver. Do not store the image."* The raw AAMVA string carries date of
     * birth and home address, and the server does not send it here.
     */
    const [licences, setLicences] = useState<LicenceSummaryDto[]>([]);
    /** ⭐ What the in-app viewer is showing. Null closes it. */
    const [viewing, setViewing] = useState<{ target: PreviewTarget; name: string } | null>(null);
    /** The dialog itself, so the preview panel can sit beside it. */
    const bodyRef = useRef<HTMLDivElement>(null);

    const load = useCallback(async () => {
        if (!appointmentId && !checkInId) return;
        setLoading(true);
        setError(null);
        try {
            /**
             * ⛔ THE DOSSIER, NOT `getAppointmentDocuments`. That call returns rows
             * whose AppointmentId is set, which is NOT everything a load has:
             * paperwork dropped onto a DRIVER CARD is filed against the ARRIVAL,
             * because a walk-in has paperwork before he has an appointment.
             *
             * ⚠️ SO THIS DIALOG WAS SHOWING A SUBSET AND SAYING "Everything filed
             * against this load". Not a crash — a confident wrong answer, of the kind
             * somebody repeats out loud to a carrier.
             *
             * ⭐ The two sets are FLATTENED into one list here on purpose: the existing
             * signed/original pairing, voiding and purge handling all work on a flat
             * list, and to a scheduler it is one pile. Where each came from is not
             * lost — `checkInId` is on the row, and the row says so.
             */
            if (!appointmentId) {
                /**
                 * ⭐ THE ARRIVAL'S OWN PAPERWORK. `listForCheckIn` on the server
                 * returns documents filed against this arrival and nothing else,
                 * which is exactly right: there is no load yet to gather from.
                 *
                 * ⚠️ NO LICENCES HERE, and that is not a gap. The dossier's licence
                 * list is built from a LOAD's arrivals; this driver's licence is one
                 * click away on his own card, behind the scan icon that already
                 * shows a dot when one is on file.
                 */
                const r = await getCheckInDocuments(await getToken(), checkInId as string);
                setDocs(r.documents);
                setLicences([]);
                return;
            }
            const r = await getAppointmentPaperworkDossier(await getToken(), appointmentId);
            // ⛔ DEDUPED — the two lists overlap. See flattenDossierDocuments.
            setDocs(flattenDossierDocuments(r));
            setLicences(r.licences);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setLoading(false);
        }
    }, [appointmentId, checkInId, getToken]);

    useEffect(() => {
        if (!open) { setDocs(null); setLicences([]); setViewing(null); setError(null); return; }
        void load();
    }, [open, load]);

    /**
     * ⭐ ORIGINALS, EACH CARRYING ITS SIGNED COPIES. `signedFromDocumentId` points
     * at the original, so a signed copy is never a top-level row.
     *
     * ⚠️ AN ORPHANED SIGNED COPY IS STILL SHOWN. If its original was voided the
     * parent is gone from this list, and dropping the copy would hide a document
     * that was genuinely handed to a carrier — the one thing this dialog exists to
     * be able to prove.
     */
    /**
     * ---- ⭐ A CONVERTED PDF AND THE IMAGE IT CAME FROM ARE ONE THING ---------
     *
     * ⛔ SAME RULE AS `PaperworkList`, and stated here because this dialog builds
     * its own list rather than using that component. ⚠️ Without it a scanned
     * JPEG shows TWICE — once as itself and once as its PDF — and a load with
     * four scans holds eight rows.
     *
     * ⭐ THE PDF IS THE ROW A PERSON SEES; the image is what draws its thumbnail.
     * The source is HIDDEN, NOT DELETED — still on the load, still in the
     * dossier, still what a dispute would be shown.
     */
    const { sourceOf, convertedSources } = useMemo(() => {
        const src = new Map<string, DocumentDto>();
        const hide = new Set<string>();
        for (const d of docs ?? []) {
            if (!d.convertedFromDocumentId) continue;
            const from = (docs ?? []).find(x => x.documentId === d.convertedFromDocumentId);
            /* ⛔ ONLY HIDE A SOURCE WE ACTUALLY FOUND. A broken pair — the image
               voided, say — must still show the PDF rather than vanish quietly. */
            if (from) { src.set(d.documentId, from); hide.add(from.documentId); }
        }
        return { sourceOf: src, convertedSources: hide };
    }, [docs]);

    const groups = useMemo<DocGroup[]>(() => {
        if (!docs) return [];
        const live = docs.filter(d => !d.voidedUtc && !convertedSources.has(d.documentId));
        const byId = new Map(live.map(d => [d.documentId, d]));
        const out: DocGroup[] = [];
        const claimed = new Set<string>();

        for (const d of live) {
            if (d.signedFromDocumentId) continue;
            const signed = live.filter(s => s.signedFromDocumentId === d.documentId);
            signed.forEach(s => claimed.add(s.documentId));
            out.push({ original: d, signed });
        }
        for (const d of live) {
            if (d.signedFromDocumentId && !claimed.has(d.documentId)
                && !byId.has(d.signedFromDocumentId)) {
                out.push({ original: d, signed: [] });
            }
        }
        return out;
    }, [docs, convertedSources]);

    const voided = useMemo(() => (docs ?? []).filter(d => d.voidedUtc), [docs]);

    async function download(d: DocumentDto) {
        setBusyId(d.documentId);
        setError(null);
        try {
            // ⛔ FETCHED ON CLICK, NEVER STORED. The link is short-lived (a user
            // delegation SAS); holding one in state would hand out a URL that has
            // since expired, or one that has not.
            const { url } = await getDocumentUrl(await getToken(), d.documentId);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setBusyId(null);
        }
    }

    async function doVoid(reason?: string) {
        if (!voiding || !reason) return;
        setBusyId(voiding.documentId);
        setError(null);
        try {
            await voidDocumentApi(await getToken(), voiding.documentId, reason);
            setVoiding(null);
            await load();
            onChanged?.();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : String(err));
        } finally {
            setBusyId(null);
        }
    }

    const row = (d: DocumentDto, isSigned: boolean) => (
        <div
            key={d.documentId}
            className={`flex items-start gap-2 p-2 ${isSigned ? 'pl-8 bg-muted/30' : ''}`}
        >
            {/**
              * ---- ⭐⭐ THE THUMBNAIL, IN THE THIRD PLACE THAT LISTS DOCUMENTS ---
              *
              * > Pete, 2026-09-10: *"no thumbnails on the jpeg images."*
              *
              * ⛔⛔ AND THIS IS §4.14.3 FOR THE FOURTH TIME. Thumbnails were built
              * into `PaperworkSliver`, `ScanFolderPanel` AND `PaperworkList` — and
              * this dialog, which is a FOURTH list of the same documents reached by
              * clicking the eye, kept a hardcoded `<FileText/>`. Every guard was
              * green because every guard was pointed at the files that had been
              * edited.
              *
              * ⚠️ THE REAL DEFECT IS THAT FOUR COMPONENTS RENDER A DOCUMENT ROW.
              * That is not fixed here and should not be pretended away — see the
              * note in `AttachedPaperworkPanel` about `PaperworkList` existing
              * precisely so there would not be a second implementation. This is the
              * cheap fix; the guard below counts the population so the next miss
              * fails a deploy instead of reaching Pete.
              *
              * ⭐ A converted PDF borrows its SOURCE IMAGE's picture — a PDF cannot
              * make one without the renderer we did not ship (§1AO), and the image
              * being kept is exactly what makes this possible.
              */}
            {isSigned
                ? <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                : <DocumentThumb
                    fileName={(sourceOf.get(d.documentId) ?? d).fileName}
                    size={28}
                  />}
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{d.fileName}</div>
                <div className="text-xs text-muted-foreground">
                    {d.documentType ?? 'untyped'} · {sizeOf(d.bytes)}
                    {d.pageCount ? ` · ${d.pageCount}p` : ''} · {when(d.receivedUtc)}
                    {d.uploadedBy ? ` · ${d.uploadedBy}` : ''}
                </div>
                {/**
                  * ⭐ WHERE IT CAME IN, SAID OUT LOUD. A document filed against the
                  * ARRIVAL rather than the load is the same paperwork to a carrier but
                  * different provenance — and provenance is the entire reason any of
                  * this is stored. Flattening the two lists without this note would
                  * throw away the one thing worth keeping about the difference.
                  */}
                {d.checkInId && (
                    <div className="mt-0.5 flex items-center gap-1 text-[11px] text-sky-700">
                        <TruckIcon className="h-3 w-3" />
                        Handed over at the gate, on the driver's arrival
                    </div>
                )}
                {/* ⭐ THE STAMP AS IT WAS PRINTED, not a re-rendered version. This is
                    the evidence that a named person signed for the appointment, and it is
                    the whole reason `SignedText` is stored rather than recomposed. */}
                {isSigned && d.signedText && (
                    <div className="mt-1 rounded border bg-background p-1.5 font-mono text-[11px] whitespace-pre-line">
                        {d.signedText}
                    </div>
                )}
                {/* ⚠️ A PURGED DOCUMENT KEEPS ITS ROW. "We had it and policy removed
                    it" must stay distinguishable from "we never had it". */}
                {d.purged && (
                    <div className="mt-1 text-xs text-amber-600">
                        The file has been purged by the retention policy. The record remains.
                    </div>
                )}
            </div>
            <div className="flex shrink-0 gap-1">
                {/**
                  * ⭐ VIEW WITHOUT LEAVING THE LOAD. The question a preview answers here
                  * is "is this the right BOL for this appointment", and the appointment
                  * is on the screen behind this dialog — a new tab hides it.
                  * ⛔ Download stays beside it: the panel draws PDFs and images, and a
                  * .docx or a .tif is real paperwork it cannot draw.
                  */}
                <Button
                    variant="ghost" size="sm" title="View"
                    disabled={d.purged}
                    onClick={() => setViewing({
                        target: { kind: 'document', documentId: d.documentId },
                        name: d.fileName,
                    })}
                >
                    <Eye className="h-3.5 w-3.5" />
                </Button>
                {/**
                  * ---- ⭐ PRINT (Pete, 2026-09-10: *"a print icon on the Paperwork
                  * popup"*) ---------------------------------------------------
                  *
                  * ⛔ IT IS NOT THE DOWNLOAD BUTTON WITH A DIFFERENT ICON. Download
                  * hands you a file; this opens the OS print dialog with the
                  * workstation's printers in it. ⚠️ The two were the same action
                  * until today, which is exactly why print appeared not to work.
                  *
                  * ⭐ A `.docx` or `.tif` falls back to a download, and the message
                  * says the desktop app is needed — see `lib/printDocument.ts`.
                  */}
                <Button
                    variant="ghost" size="sm" title="Print"
                    disabled={d.purged}
                    onClick={() => void (async () => {
                        setPrintMsg(null);
                        const outcome = await printDocumentToDialog(
                            getToken, { kind: 'document', documentId: d.documentId }, d.fileName);
                        setPrintMsg(outcome.message);
                    })()}
                >
                    <Printer className="h-3.5 w-3.5" />
                </Button>
                <Button
                    variant="ghost" size="sm" title="Download"
                    disabled={busyId === d.documentId || d.purged}
                    onClick={() => void download(d)}
                >
                    {busyId === d.documentId
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Download className="h-3.5 w-3.5" />}
                </Button>
                {/* ⛔ ONLY AN ORIGINAL CAN BE SIGNED. Signing a signed copy would
                    stamp a stamp, and the pairing would no longer say which document
                    the carrier was actually given. */}
                {!isSigned && !d.purged && (
                    <Button
                        variant="ghost" size="sm" title="Sign as received"
                        disabled={busyId !== null}
                        onClick={() => setSigning(d)}
                    >
                        <PenLine className="h-3.5 w-3.5" />
                    </Button>
                )}
                <Button
                    variant="ghost" size="sm" title="Remove from this appointment"
                    disabled={busyId !== null}
                    onClick={() => setVoiding(d)}
                >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
            </div>
        </div>
    );

    return (
        <>
            <Dialog open={open} onOpenChange={(o) => !o && busyId === null && onClose()}>

                {/**
                  * ⛔ THE DOCUMENT PREVIEW IS NOT "OUTSIDE", EVEN THOUGH THE DOM SAYS SO.
                  *
                  * ⚠️ `DocumentPreview` portals itself to document.body so it can sit
                  * BESIDE this dialog rather than inside it — that placement is the
                  * whole point, you are comparing paperwork against the load. But Radix
                  * closes a dialog on any pointer event outside its own subtree, and a
                  * portal is outside by definition. So scrolling the PDF, or pressing
                  * the preview's own X, read as "the user clicked away" and tore this
                  * dialog down underneath it.
                  *
                  * Pete, 2026-08-25: *"When I scroll on the new dock to see the
                  * signature, everything closes... when I 'x' out of the pdf, it doesn't
                  * go back to the previous popup, it closes both."* Two symptoms, one
                  * cause.
                  *
                  * ⭐ ESCAPE IS HANDLED THE SAME WAY AND FOR THE SAME REASON: with a
                  * preview open, Escape belongs to the preview. Letting it through would
                  * close both at once, which is the bug in its keyboard form.
                  */}
                <DialogContent
                    className="max-w-2xl"
                    onPointerDownOutside={(e) => {
                        if ((e.target as HTMLElement | null)?.closest('[data-document-preview]')) e.preventDefault();
                    }}
                    onInteractOutside={(e) => {
                        if ((e.target as HTMLElement | null)?.closest('[data-document-preview]')) e.preventDefault();
                    }}
                    onEscapeKeyDown={(e) => { if (viewing) { e.preventDefault(); setViewing(null); } }}
                >
                    <DialogHeader>
                        <DialogTitle>
                            Paperwork{appointmentNumber ? ` · ${appointmentNumber}` : ''}
                        </DialogTitle>
                        <DialogDescription>
                            Everything filed against this appointment. Scanning another ADDS a
                            document — nothing here is overwritten in place, so an
                            earlier scan stays available until somebody removes it and
                            says why.
                        </DialogDescription>
                    </DialogHeader>

                    {error && (
                        <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div ref={bodyRef} className="space-y-3">

                    {/**
                      * ---- ⭐ THE DRIVERS' LICENCES ------------------------------
                      *
                      * ⛔ FIELDS, NOT AN IMAGE. Pete settled this when the licence rules
                      * were written: decode it and store the data, do not store the
                      * picture. So there is nothing here to open — this IS the record.
                      *
                      * ⚠️ "TYPED, NOT SCANNED" IS THE MOST IMPORTANT LINE ON IT. A
                      * scheduler keying details because the driver had no licence in
                      * hand is a different grade of evidence, and outbound rules turn
                      * on the difference. Shown in amber precisely so nobody reads a
                      * typed record as a scan.
                      */}
                    {licences.length > 0 && (
                        <div className="rounded-md border">
                            <div className="flex items-center gap-1.5 border-b px-2 py-1.5 text-xs font-medium">
                                <CreditCard className="h-3.5 w-3.5" />
                                Driver license{licences.length > 1 ? 's' : ''}
                            </div>
                            <div className="divide-y">
                                {licences.map(l => (
                                    <div key={l.checkInId} className="px-2 py-1.5 text-xs">
                                        <div className="flex flex-wrap items-baseline gap-x-2">
                                            <span className="font-medium">
                                                {[l.firstName, l.lastName].filter(Boolean).join(' ')
                                                    || l.checkInName || 'Driver'}
                                            </span>
                                            {l.arrivalSeq != null && (
                                                <span className="text-muted-foreground">
                                                    truck {l.arrivalSeq}
                                                </span>
                                            )}
                                            {l.isManualEntry && (
                                                <span className="rounded bg-amber-100 px-1 font-medium text-amber-900">
                                                    typed, not scanned
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                                            {l.licenceNumber
                                                ? `${l.licenceState ? l.licenceState + ' ' : ''}${l.licenceNumber}`
                                                : 'no license number on file'}
                                            {l.vehicleClass ? ` · class ${l.vehicleClass}` : ''}
                                            {l.endorsements ? ` · ${l.endorsements}` : ''}
                                            {l.licenceExpiry ? ` · expires ${l.licenceExpiry}` : ''}
                                        </div>
                                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                                            {l.scannedUtc
                                                ? `Scanned ${when(l.scannedUtc)}${l.scannedBy ? ` by ${l.scannedBy}` : ''}`
                                                : 'Not scanned'}
                                            {/* ⭐ Last use — Pete asked for it. Derived
                                                from the gate log, not a stored column. */}
                                            {l.lastSeenUtc ? ` · last here ${when(l.lastSeenUtc)}` : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {loading && !docs ? (
                        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </div>
                    ) : groups.length === 0 ? (
                        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                            {/**
                              * ⭐ IT SAYS WHERE PAPERWORK COMES FROM, because "nothing
                              * here" with no next step leaves a scheduler hunting for a
                              * scan button that deliberately does not exist.
                              */}
                            Nothing filed against this appointment yet. Scans arrive in the
                            Scanned Paperwork tray in the arrivals column — drag one onto
                            this appointment or onto its driver to file it.
                        </div>
                    ) : (
                        <div className="max-h-[50vh] divide-y overflow-y-auto rounded-md border">
                            {groups.map(g => (
                                <div key={g.original.documentId} className="divide-y">
                                    {row(g.original, !!g.original.signedFromDocumentId)}
                                    {g.signed.map(s => row(s, true))}
                                </div>
                            ))}
                        </div>
                    )}

                    {/**
                      * ---- ⭐⭐ SOMEWHERE TO PUT A FILE, ON THE POPUP ITSELF --------
                      *
                      * > Pete, 2026-09-10: *"Still no place for me to attach paperwork
                      * > on the popup."*
                      *
                      * ⛔ HE WAS RIGHT AND THE EMPTY-STATE TEXT ABOVE PROVES IT — it
                      * tells a scheduler to go and drag something from the tray in the
                      * arrivals column, because until now that WAS the only way. That is
                      * a fine sentence for "nothing is filed yet" and a poor answer to
                      * "I have the file right here."
                      *
                      * ⭐ IT IS `FileDropZone`, THE SHARED CONTROL — the same one
                      * `PaperworkList` uses, uploading through the same `uploadDocument`
                      * with the same `source: 'Upload'`. ⛔ A hand-rolled input here
                      * would be a second upload path onto a load, and the two would
                      * disagree about naming, about conversion, and about what an
                      * upload records — which is exactly how this dialog ended up with
                      * no thumbnails while three other lists had them.
                      *
                      * ⚠️ GATED ON HAVING SOMEWHERE TO SEND IT. A drop zone with no
                      * appointment or no facility is a drop that fails at the server.
                      */}
                    {appointmentId && facilityCode && (
                        <FileDropZone
                            getToken={getToken}
                            label="Add a file from this machine — drop it here, or browse"
                            upload={(token, file) => uploadDocument(token, facilityCode, file, {
                                appointmentId,
                                source: 'Upload',
                            })}
                            onDone={async () => { await load(); onChanged?.(); }}
                        />
                    )}

                    {/**
                      * ---- ⚠️ TRUCK PHOTOS: THE SECTION EXISTS, THE PHOTOS DO NOT ----
                      *
                      * Pete, 2026-08-25: *"we will start to take an image of the trucks
                      * arriving and departing via the laborclock app in pipeline. These
                      * images will need to be saved to the load or unload appointment."*
                      *
                      * ⛔ SAYING SO IS NOT CLUTTER, IT IS THE POINT. A scheduler who
                      * expects photos and sees no section cannot tell "none were taken
                      * for this load" from "this screen does not show photos" — and the
                      * second sends somebody to IT. One quiet line answers both.
                      *
                      * ⭐ It disappears by itself: once #202 lands, photos render here
                      * and this line stops being the whole section.
                      */}
                    <div className="flex items-center gap-1.5 rounded-md border border-dashed px-2 py-1.5
                                    text-[11px] text-muted-foreground">
                        <Camera className="h-3.5 w-3.5 shrink-0" />
                        No truck photos on this appointment. Arrival and departure photos start
                        when the labor clock app begins sending them.
                    </div>

                    </div>

                    {printMsg && (
                        <div className="flex items-start justify-between gap-2 rounded border
                                        bg-muted/40 px-2 py-1 text-[11px]">
                            <span>{printMsg}</span>
                            <button type="button" onClick={() => setPrintMsg(null)}
                                    className="shrink-0 underline">dismiss</button>
                        </div>
                    )}

                    {/* ⚠️ VOIDED DOCUMENTS ARE LISTED, not erased. Somebody removed a
                        document from this appointment and gave a reason; that is a fact about
                        the appointment, and hiding it makes a scheduler wonder where a scan
                        they remember filing went. */}
                    {voided.length > 0 && (
                        <details className="rounded-md border p-2 text-sm">
                            <summary className="cursor-pointer text-muted-foreground">
                                {voided.length} removed
                            </summary>
                            <div className="mt-2 space-y-1">
                                {voided.map(d => (
                                    <div key={d.documentId} className="text-xs text-muted-foreground">
                                        <span className="line-through">{d.fileName}</span>
                                        {' — '}{d.voidReason || 'no reason given'} · {when(d.voidedUtc)}
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}

                    <DialogFooter>
                        <div className="flex w-full items-center justify-between gap-2">
                            <div className="flex gap-2">
                                <Button
                                    variant="ghost" size="sm" disabled={loading}
                                    onClick={() => void load()}
                                >
                                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                                    Refresh
                                </Button>
                            </div>
                            <Button size="sm" onClick={onClose} disabled={busyId !== null}>
                                Done
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/**
              * ⭐ BESIDE THE DIALOG, NOT INSIDE IT. DocumentPreview portals itself to
              * the body and places itself against this anchor, so the document sits
              * next to the list rather than replacing it — which is the whole point:
              * you are comparing the paperwork against the load.
              */}
            <DocumentPreview
                anchor={bodyRef.current}
                target={viewing?.target ?? null}
                name={viewing?.name ?? ''}
                getToken={getToken}
                onClose={() => setViewing(null)}
                onDownload={() => {
                    const d = (docs ?? []).find(x => viewing?.target.kind === 'document'
                        && x.documentId === viewing.target.documentId);
                    if (d) void download(d);
                }}
            />

            <SignDocumentDialog
                open={signing !== null}
                document={signing}
                facilityCode={facilityCode}
                carrierCode={carrierCode}
                appointmentNumber={appointmentNumber}
                getToken={getToken}
                onClose={() => setSigning(null)}
                onSigned={() => {
                    setSigning(null);
                    // The signed copy is a SECOND row — reload rather than patching
                    // state, so the pairing is built by the one piece of code that
                    // knows how (see `groups`).
                    void load();
                    onChanged?.();
                }}
            />

            <ConfirmDialog
                open={voiding !== null}
                title="Remove this document from the appointment?"
                message={voiding
                    ? `${voiding.fileName} stays in storage and in the record — it is `
                      + 'removed from this appointment, not deleted.'
                    : ''}
                confirmLabel="Remove"
                destructive
                // ⛔ A REASON IS REQUIRED. Paperwork disappearing from a load with no
                // explanation is exactly the gap a carrier dispute lands in.
                requireReason
                reasonLabel="Why is it being removed?"
                reasonPlaceholder="e.g. wrong load — belongs to A00000131"
                busy={busyId !== null}
                onConfirm={(reason) => void doVoid(reason)}
                onCancel={() => setVoiding(null)}
            />
        </>
    );
}
